/**
 * Box - Universal Container Component
 *
 * The most primitive building block. A polymorphic container that can render as any HTML element.
 * ALL layout and container components should build upon Box.
 *
 * @example
 * <Box>Default div container</Box>
 * <Box as="section" className="bg-gray-100">Section container</Box>
 * <Box as="article">Article with padding</Box>
 */
import React from "react";
import { cn } from "@/shared/lib/utils/cn";
import type { PolymorphicProps } from "./types";

// Default props for Box
interface BoxOwnProps {
  /** Additional CSS classes */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Test ID for testing */
  "data-testid"?: string;
  /** Child content */
  children?: React.ReactNode;
}

// Polymorphic Box props - defaults to div but can be any element
export type BoxProps<E extends React.ElementType = "div"> = PolymorphicProps<
  E,
  BoxOwnProps
>;

/**
 * Box component with polymorphic "as" prop
 *
 * Usage:
 * - <Box /> renders a div
 * - <Box as="section" /> renders a section
 * - <Box as={Link} to="/home" /> renders a Link component
 */
export function Box<E extends React.ElementType = "div">({
  as,
  className,
  children,
  ...props
}: BoxProps<E>) {
  // Default to div if no "as" prop provided
  const Component = as || "div";

  return (
    <Component className={cn(className)} {...props}>
      {children}
    </Component>
  );
}

Box.displayName = "Box";
